import React, { useState, useRef, useEffect } from 'react';
import { PDF_EXPORT_CLOSE_DROPDOWNS_EVENT } from '../helpers/downloadPageAsPDF';

type MultiselectFilterProps = {
  options: string[];
  onApply: (items: string[]) => void;
  selectedItems?: string[];
  title?: string;
  placeholder?: string;
  itemLabel?: string;
  minWidth?: string;
  maxWidth?: string;
};

const limitStringLength = (str: string, length: number) => {
  return str.length > length ? str.substring(0, length) + "..." : str;
};

const MultiselectFilter: React.FC<MultiselectFilterProps> = ({ 
  options = [], 
  onApply,
  selectedItems = [],
  title = 'Select Items',
  placeholder = 'Select Items',
  itemLabel = 'items',
  minWidth,
  maxWidth
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>(selectedItems);
  const [tempSelected, setTempSelected] = useState<string[]>(selectedItems);
  const [retryCount, setRetryCount] = useState(0);
  const [searchText, setSearchText] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelected(selectedItems);
    setTempSelected(selectedItems);
  }, [selectedItems]);

  useEffect(() => {
    // Reset search when dropdown closes
    if (!isOpen) {
      setSearchText('');
    }
  }, [isOpen]);

  // Reset temp selection when options changes to ensure consistency
  useEffect(() => {
    if (options && options.length > 0) {
      // Only keep selected items that are still available
      const validSelected = selectedItems.filter(item => 
        options.includes(item)
      );
      
      // Only update if there's actually a change in valid items AND dropdown is not open
      if (validSelected.length !== selectedItems.length && !isOpen) {
        setSelected(validSelected);
        setTempSelected(validSelected);
        
        // If we had invalid selections, update the parent
        onApply(validSelected);
      }
      
      // Reset retry count when we get data
      setRetryCount(0);
    }
  }, [options, selectedItems, onApply, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const closeDropdown = () => setIsOpen(false);

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener(PDF_EXPORT_CLOSE_DROPDOWNS_EVENT, closeDropdown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener(PDF_EXPORT_CLOSE_DROPDOWNS_EVENT, closeDropdown);
    };
  }, []);

  const handleOptionToggle = (option: string) => {
    setTempSelected(prev => 
      prev.includes(option)
        ? prev.filter(val => val !== option)
        : [...prev, option]
    );
  };

  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleSelectAll = () => {
    if (filteredOptions && filteredOptions.length > 0) {
      // Check if all filtered options are already selected
      const allFilteredSelected = filteredOptions.every(opt => tempSelected.includes(opt));
      if (allFilteredSelected) {
        // If all filtered selected, deselect only filtered ones
        setTempSelected(prev => prev.filter(opt => !filteredOptions.includes(opt)));
      } else {
        // Otherwise select all filtered options
        const newSelected = Array.from(new Set([...tempSelected, ...filteredOptions]));
        setTempSelected(newSelected);
      }
    }
  };

  const handleClear = () => {
    setTempSelected([]);
  };

  const isAllSelected = filteredOptions.length > 0 && filteredOptions.every(opt => tempSelected.includes(opt));

  const handleApply = () => {
    setSelected(tempSelected);
    onApply(tempSelected);
    setSearchText('');
    setIsOpen(false);
  };

  const handleCancel = () => {
    setTempSelected(selected);
    setSearchText('');
    setIsOpen(false);
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    window.location.reload();
  };

  const getDisplayText = () => {
    if (selected.length === 0) {
      return placeholder;
    }
    if (selected.length === 1) {
      return limitStringLength(selected[0], 20);
    }
    return `${selected.length} ${itemLabel} selected`;
  };

  if (!options || options.length === 0) {
    return (
      <div className="multiselect-filter-wrapper">
        <div className="multiselect-filter-trigger loading">
          <span className="placeholder">
            {retryCount > 0 ? `No ${itemLabel} found` : `Loading ${itemLabel}...`}
          </span>
          {retryCount > 0 && (
            <button 
              onClick={handleRetry}
              className="retry-button"
              title={`Retry loading ${itemLabel}`}
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="multiselect-filter-wrapper"
      ref={dropdownRef}
      style={{ minWidth, maxWidth }}
    >
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="multiselect-filter-trigger"
        title={selected.join(', ')}
      >
        <span className={selected.length === 0 ? 'placeholder' : 'selected-text'}>
          {getDisplayText()
          //  .replace(/([a-z])([A-Z])/g, '$1 $2')
          //  .replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1))
          }
        </span>
      </div>
      
      {isOpen && (
        <div className="multiselect-filter-dropdown">
          <div className="multiselect-filter-header">
            <h4>{
              title
              //.replace(/([a-z])([A-Z])/g, '$1 $2')
              //.replace(/\w\S*/g, w => w.charAt(0)
              //.toUpperCase() + w.slice(1))
            }</h4>
            {/* <span className="multiselect-filter-selection-count">
              {tempSelected.length} selected
            </span> */}
            <button 
              onClick={handleClear}
              className="filter-clear-btn"
              title="Clear selection"
            >
              Clear
            </button>
          </div>
          
          {/* Select All and Search in same line */}
          <div className="multiselect-filter-select-all-search-row">
            {/* Select All option */}
            <div
              onClick={handleSelectAll}
              className={`multiselect-filter-option-item multiselect-filter-select-all ${isAllSelected ? 'selected' : ''}`}
              title="Select All"
            >
              <div className="multiselect-filter-checkbox">
                {isAllSelected && (
                  <span className="multiselect-filter-checkmark">✓</span>
                )}
              </div>
              <span className="multiselect-filter-option-name">
                Select All
              </span>
            </div>
            
            {/* Search input */}
            <div className="multiselect-filter-search-container">
              <input
                type="text"
                className="multiselect-filter-search-input"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </div>
          </div>
          
          <div className="multiselect-filter-options-list">
            {filteredOptions.map((option, idx) => (
              <div
                key={`${option}-${idx}`}
                onClick={() => handleOptionToggle(option)}
                className={`multiselect-filter-option-item ${tempSelected.includes(option) ? 'selected' : ''}`}
                title={option}
              >
                <div className="multiselect-filter-checkbox">
                  {tempSelected.includes(option) && (
                    <span className="multiselect-filter-checkmark">✓</span>
                  )}
                </div>
                <span className="multiselect-filter-option-name">
                  {limitStringLength(option, 30)}
                </span>
              </div>
            ))}
          </div>

          <div className="multiselect-filter-footer">
            <button
              onClick={handleCancel}
              className="multiselect-filter-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="multiselect-filter-apply-btn"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiselectFilter;

